import { useState, useEffect, useRef } from 'react'
import { staticAssetsApi } from '../services/api'
import {
  Button,
  Card,
  CardBody,
  ErrorMessage,
  SuccessMessage,
  LoadingSpinner,
  EmptyState
} from './common'

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
const MAX_SIZE = 5 * 1024 * 1024 // 5MB

const AssetsTab = () => {
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    loadAssets()
  }, [])

  const loadAssets = async () => {
    try {
      setLoading(true)
      const response = await staticAssetsApi.list({ category: 'email-branding', pageSize: 100 })
      const result = response.data?.data || response.data?.items || []
      setAssets(Array.isArray(result) ? result : [])
    } catch {
      setError('Failed to load assets')
      setAssets([])
    } finally {
      setLoading(false)
    }
  }

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Invalid file type. Allowed: PNG, JPEG, GIF, WebP')
      return
    }
    if (file.size > MAX_SIZE) {
      setError('File too large. Maximum size: 5MB')
      return
    }

    try {
      setUploading(true)
      setError(null)
      const formData = new FormData()
      formData.append('File', file)
      formData.append('Category', 'email-branding')

      await staticAssetsApi.upload(formData)
      setSuccess('Asset uploaded successfully')
      setTimeout(() => setSuccess(null), 3000)
      await loadAssets()
    } catch (err) {
      setError(err.response?.data || 'Failed to upload asset')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return
    try {
      await staticAssetsApi.delete(id)
      setSuccess('Asset deleted')
      setTimeout(() => setSuccess(null), 3000)
      await loadAssets()
    } catch {
      setError('Failed to delete asset')
    }
  }

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url)
    setSuccess('URL copied to clipboard')
    setTimeout(() => setSuccess(null), 2000)
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return '—'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (loading) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess(null)} />}

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Upload images for use in email templates (logos, banners, icons).
        </p>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
          />
          <Button
            variant="primary"
            onClick={() => fileInputRef.current?.click()}
            loading={uploading}
          >
            Upload Image
          </Button>
        </div>
      </div>

      {assets.length === 0 ? (
        <EmptyState message="No assets uploaded yet." icon="🖼" />
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
          {assets.map(asset => (
            <Card key={asset.staticAssetId || asset.id}>
              <CardBody className="p-3">
                <div className="flex items-center justify-center bg-gray-50 rounded-lg mb-3" style={{ height: '140px' }}>
                  <img
                    src={asset.url || asset.fileUrl}
                    alt={asset.name || asset.fileName}
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>' }}
                  />
                </div>
                <p className="text-sm font-medium text-gray-900 truncate" title={asset.name || asset.fileName}>
                  {asset.name || asset.fileName}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(asset.fileSize || asset.size)}
                  {(asset.contentType || asset.mimeType) && ` - ${(asset.contentType || asset.mimeType).split('/')[1]?.toUpperCase()}`}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="ghost"
                    size="small"
                    className="flex-1"
                    onClick={() => handleCopyUrl(asset.url || asset.fileUrl)}
                  >
                    Copy URL
                  </Button>
                  <Button
                    variant="danger"
                    size="small"
                    onClick={() => handleDelete(asset.staticAssetId || asset.id)}
                  >
                    Delete
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export default AssetsTab
